import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from "@angular/common/http";
import {MatSnackBar} from "@angular/material/snack-bar";
import {HelperService} from "../../helper.service";
import {catchError, Observable, retry} from "rxjs";
import {BaseService} from "../../services/base.service";
import {PlantsModel} from "../models-otc/plants-model";
import {ItemModel} from "../models-otc/item-model";
import {ItemsDto} from "../dto-otc/items-dto";
import {UpdateItemModel} from "../models-otc/update-item-model";
import {MaterialModel} from "../models-otc/material-model";
import {DiscountModel} from "../models-otc/discount-model";
import { DeliveryLookup } from '../models-otc/deliveryLookup-model';
import { AgentModel } from '../models-otc/agent-model';
import { User } from 'src/app/models/user';
import { OrdinaryMaterialModel } from '../models-otc/ordinary-material-model';

@Injectable({
    providedIn: 'root'
})
export class ItemService extends BaseService {

    constructor(
        private httpClient: HttpClient,
        public snackBar: MatSnackBar,
        public helper: HelperService,
    ) {
        super();
    }

/// GET ///

    getPlantsList(): Observable<PlantsModel[]> {
        return this.httpClient.get<PlantsModel[]>(this.OTC_BASE_URL + `plants`)
            .pipe(
                retry(0),
                catchError((err) => {
                    this.hasError.next(true);
                    this.snackBar.open('Error Retrieving Plants','close', {
                        duration: 5000,
                    });
                    this.hasError.next(false);
                    return this.errorHandler(err);
                })
            );
    }

    getOtcAgents(): Observable<AgentModel[]> {
        return this.httpClient.get<AgentModel[]>(this.OTC_BASE_URL + 'agents');
    }

    getOtcAgentsForOthers(userId): Observable<AgentModel[]> {
        let params = new HttpParams().append('userId', userId);
        return this.httpClient.get<AgentModel[]>(this.OTC_BASE_URL + 'agents/others', {params});
    }

    getCreatedByUsers(userId: number): Observable<User[]> {
         let params = new HttpParams();

        if (userId) {
            params = params.append('userId', userId);
        }
        return this.httpClient.get<User[]>(this.OTC_BASE_URL + 'orders/createdByUsers', {params});
    }

    getFullMaterialList(): Observable<OrdinaryMaterialModel[]> {
        return this.httpClient.get<OrdinaryMaterialModel[]>(this.OTC_BASE_URL + 'materials');
    }

    getMaterialList(plantId: number, date: string, customerId :number): Observable<MaterialModel[]> {
        return this.httpClient.get<MaterialModel[]>(this.OTC_BASE_URL + `materials/${plantId}/products?date=${date}&customerId=${customerId}`)
            .pipe(
                retry(0),
                catchError((err) => {
                    this.hasError.next(true);
                    this.snackBar.open('Error Retrieving Materials','close', {
                        duration: 5000,
                    });
                    this.hasError.next(false);
                    return this.errorHandler(err);
                })
            );
    }


    getMaterialProduct(materialPricingId: number): Observable<MaterialModel> {
        return this.httpClient.get<MaterialModel>(this.OTC_BASE_URL + `materials/product/${materialPricingId}`)
            .pipe(
                retry(0),
                catchError((err) => {
                    this.hasError.next(true);
                    this.snackBar.open('Error Retrieving Materials','close', {
                        duration: 5000,
                    });
                    this.hasError.next(false);
                    return this.errorHandler(err);
                })
            );
    }


    getItemList(orderId:number): Observable<ItemModel[]> {
        return this.httpClient.get<ItemModel[]>(this.OTC_BASE_URL + `orders/${orderId}/items`)
            .pipe(
                retry(0),
                catchError((err) => {
                    this.hasError.next(true);
                    this.snackBar.open('Error Retrieving Order Items','close', {
                        duration: 5000,
                    });
                    this.hasError.next(false);
                    return this.errorHandler(err);
                })
            );
    }


    getListItemById(itemId:number): Observable<ItemModel> {
        return this.httpClient.get<ItemModel>(this.OTC_BASE_URL + `orders/items/${itemId}`)
            .pipe(
                retry(0),
                catchError((err) => {
                    this.hasError.next(true);
                    this.snackBar.open('Error Retrieving Order Items','close', {
                        duration: 5000,
                    });
                    this.hasError.next(false);
                    return this.errorHandler(err);
                })
            );
    }

    getDiscountValidationList(orderId:number): Observable<DiscountModel[]> {
        return this.httpClient.get<DiscountModel[]>(this.OTC_BASE_URL + `orders/${orderId}/validateitems`)
            .pipe(
                retry(0),
                catchError((err) => {
                    this.hasError.next(true);
                    this.snackBar.open('Error Retrieving Discount Validation Items','close', {
                        duration: 5000,
                    });
                    this.hasError.next(false);
                    return this.errorHandler(err);
                })
            );
    }

    getDeliveryLookup(radius:number, ysap: string): Observable<DeliveryLookup> {
        return this.httpClient.get<DeliveryLookup>(this.OTC_BASE_URL + `deliverylookup/${radius}?YSAP=${ysap}`)
            .pipe(
                retry(0),
                catchError((err) => {
                    this.hasError.next(true);
                    this.snackBar.open('Error Retrieving Delivery Lookup Values','close', {
                        duration: 5000,
                    });
                    this.hasError.next(false);
                    return this.errorHandler(err);
                })
            );
    }


    /// POST ///

    postNewItem(itemsDto: ItemsDto): Observable<UpdateItemModel> {
        return this.httpClient.post<UpdateItemModel>(this.OTC_BASE_URL + 'orders/items', itemsDto)
            .pipe(
                retry(0),
                catchError((err) => {
                    this.hasError.next(true);
                    this.snackBar.open('Error Creating New Line Item','close', {
                        duration: 5000,
                    });
                    this.hasError.next(false);
                    return this.errorHandler(err);
                })
            );
    }


/// PUT ///
    updateOrderItemById(itemsDto: ItemsDto): Observable<UpdateItemModel> {
        return this.httpClient.put<UpdateItemModel>(this.OTC_BASE_URL +`orders/items`, itemsDto)
            .pipe(
                retry(0),
                catchError((err) => {
                    this.hasError.next(true);
                    this.snackBar.open('Error Updating New Line Item','close', {
                        duration: 5000,
                    });
                    this.hasError.next(false);
                    return this.errorHandler(err);
                })
            );
    }


//  DELETE ///
    deleteOrderListItemById(id: number): Observable<string> {
        return this.httpClient.delete(this.OTC_BASE_URL + `orders/items/${id}`, { responseType: 'text'})
            .pipe(
                retry(0),
                catchError((err) => {
                    this.hasError.next(true);
                    this.snackBar.open('Error Removing  Item / Items','close', {
                        duration: 5000,
                    });
                    this.hasError.next(false);
                    return this.errorHandler(err);
                })
            );
    }
}
